/**
 * BLU-1400: the client's copies of its 3PL's garments (supply aliases) on
 * Products, read-only. One block per 3PL ("On Threadheads's garments"), one
 * table per block: product-code rows that expand to their sizes and colours,
 * the same chevron and sub-row shape as the grouped products table above.
 *
 * The 3PL keeps these garments' details, so there is no checkbox, no edit
 * pencil and no bulk verb here, and never the client's Default cost. A row
 * opens the read-only detail. The client's price from the 3PL (and the 3PL's
 * printing price, once per block) shows only when the list read carried it.
 *
 * Client view (a 3PL reading a client's catalog): rows do not open, the view
 * button is hidden and no price is shown, as with the ungrouped rows.
 *
 * Reads nothing: the rows are the `supplyAliasSkus` of the one Products read.
 * Expand state is the page's (`expanded` / `onToggleExpand`), so it resets
 * with the products table's.
 */
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { NoteLine } from '@/components/shared/NoteLine';
import ProductImageWithHover from '@/features/purchase-orders/components/ProductImageWithHover';
import { cn } from '@/lib/utils';
import { formatSupplyPrice, groupSupplyAliases, type SupplyAliasRow } from '../../utils/supplyAliasGroups';
import { SupplyAliasDetailDialog } from './SupplyAliasDetailDialog';

export interface SupplyAliasSectionProps {
  rows: SupplyAliasRow[];
  clientView: boolean;
  /** The page's expand keys; this section's keys start with `supply:`. */
  expanded: Set<string>;
  onToggleExpand: (key: string) => void;
}

/** Image, SKU, title, size, colour after the chevron column. */
const BASE_COLUMNS = 6;

export function SupplyAliasSection({ rows, clientView, expanded, onToggleExpand }: SupplyAliasSectionProps) {
  const { t } = useTranslation('inventory');
  const groups = useMemo(() => groupSupplyAliases(rows), [rows]);
  // The row stays set while the dialog closes, so it animates out with its content.
  const [viewing, setViewing] = useState<SupplyAliasRow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  if (groups.length === 0) return null;

  const openDetail = (row: SupplyAliasRow) => {
    setViewing(row);
    setDetailOpen(true);
  };

  return (
    <>
      {groups.map((group) => {
        const owner = group.name;
        const showPrices = !clientView && group.pricesShown && owner !== null;
        const columns = BASE_COLUMNS + (showPrices ? 1 : 0) + (clientView ? 0 : 1);
        return (
          <section key={group.key} className="space-y-2">
            <div className="space-y-0.5">
              <h3 className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  {owner !== null
                    ? t('products.supplyAlias.sectionTitle', { owner, count: group.count })
                    : t('products.supplyAlias.sectionTitleNoOwner', { count: group.count })}
                </span>
              </h3>
              <NoteLine as="p">
                {owner !== null
                  ? t('products.supplyAlias.sectionHelp', { owner })
                  : t('products.supplyAlias.sectionHelpNoOwner')}
              </NoteLine>
              {showPrices && group.printingPrice != null && (
                <NoteLine as="p" className="tabular-nums">
                  {t('products.supplyAlias.printingPrice', { price: formatSupplyPrice(group.printingPrice, group.currency) })}
                </NoteLine>
              )}
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead className="w-16">{t('table.image')}</TableHead>
                    <TableHead>{t('sku')}</TableHead>
                    <TableHead>{t('table.title')}</TableHead>
                    <TableHead>{t('size')}</TableHead>
                    <TableHead>{t('color')}</TableHead>
                    {showPrices && (
                      <TableHead className="text-right">{t('products.supplyAlias.priceColumn')}</TableHead>
                    )}
                    {!clientView && <TableHead className="w-16">{t('consumables.actions')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.codes.map((code) => {
                    const key = `supply:${group.key}:${code.code}`;
                    const isExpanded = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => onToggleExpand(key)}>
                          <TableCell className="p-2 text-center">
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              aria-label={t('products.supplyAlias.expandAria', { code: code.code })}
                              onClick={(e) => {
                                // The row toggles too: one toggle per click.
                                e.stopPropagation();
                                onToggleExpand(key);
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <ChevronDown
                                className={cn(
                                  'h-4 w-4 transition-transform duration-200 motion-reduce:transition-none',
                                  !isExpanded && '-rotate-90',
                                )}
                                aria-hidden="true"
                              />
                            </button>
                          </TableCell>
                          <TableCell className="p-2">
                            <ProductImageWithHover
                              imageUrl={code.imageUrl ?? undefined}
                              productSku={code.code}
                              size="large"
                            />
                          </TableCell>
                          <TableCell colSpan={2}>
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                              <span className="font-mono text-sm font-medium">{code.code}</span>
                              {code.name && <span className="truncate font-medium">{code.name}</span>}
                            </div>
                          </TableCell>
                          <TableCell colSpan={columns - 4} className="text-xs text-muted-foreground tabular-nums">
                            {t('products.supplyAlias.variantCount', { count: code.variants.length })}
                          </TableCell>
                        </TableRow>

                        {isExpanded &&
                          code.variants.map((variant) => (
                            <TableRow
                              key={variant.id}
                              className={cn('bg-muted/20 hover:bg-muted/40', !clientView && 'cursor-pointer')}
                              onClick={clientView ? undefined : () => openDetail(variant)}
                            >
                              <TableCell />
                              <TableCell className="p-2">
                                <ProductImageWithHover
                                  imageUrl={variant.imageUrl ?? code.imageUrl ?? undefined}
                                  productSku={variant.sku}
                                  size="large"
                                />
                              </TableCell>
                              <TableCell className="font-mono text-sm">{variant.sku}</TableCell>
                              <TableCell className="max-w-[280px] truncate">{variant.title || '-'}</TableCell>
                              <TableCell>{variant.size || '-'}</TableCell>
                              <TableCell>{variant.color || '-'}</TableCell>
                              {showPrices && (
                                <TableCell className="text-right text-sm tabular-nums">
                                  {variant.supplyPrice != null ? (
                                    formatSupplyPrice(variant.supplyPrice, group.currency)
                                  ) : (
                                    <span className="text-muted-foreground">
                                      {t('products.supplyAlias.priceNotSet')}
                                    </span>
                                  )}
                                </TableCell>
                              )}
                              {!clientView && (
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      // The row opens it too: one open per click.
                                      e.stopPropagation();
                                      openDetail(variant);
                                    }}
                                    title={t('products.supplyAlias.view')}
                                    aria-label={t('products.supplyAlias.viewAria', { sku: variant.sku })}
                                  >
                                    <Eye className="h-4 w-4" aria-hidden="true" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </section>
        );
      })}
      <SupplyAliasDetailDialog open={detailOpen} onOpenChange={setDetailOpen} row={viewing} />
    </>
  );
}
